// What a month has to bill: every recurring, usage and cost-recovery charge it
// owes, one card per customer.
//
// EVERY OTHER BILLING SCREEN IS ORGANISED BY DUE DATE — the day a document
// leaves. This one is organised by the month a charge is FOR, and on anything
// billed in arrears those are different months: TransAlta's August service goes
// out on 1 September, so a screen keyed on the date shows it under September and
// a person asking what August owes cannot find it. That is the whole reason this
// page exists beside /billing rather than as a filter on it.
//
// ONE CARD PER CUSTOMER, and the card is the unit of work. A customer's month
// adds up where you are reading it, says how much of itself is priced, and can
// be finished without scrolling anywhere else. "Kingsport's August" is a thing a
// person holds in their head; "Hansen's August" is five of them at once, and the
// payer is a routing fact about which document a line lands on. So the customer
// is the card's title and the payer rides underneath — the same editorial
// decision the bill-run screen made, for the same reason.
//
// TWO STATES PER LINE, NOT EIGHT. The report still distinguishes eight needs and
// they all still show, but as a sentence under the charge rather than as a chip
// in a column of its own: what a reader is actually sorting rows into is "this
// has its number" and "this does not", and the second kind carries the control
// that finishes it. The seven-way vocabulary belonged to a screen that could
// only describe its rows; this one can act on them.
//
//   A FACT I OWE         the rule reads a fact nobody has recorded — a meter
//                        reading, a partner's royalty figure, an infrastructure
//                        invoice. `Record a fact` is the ONE control, because a
//                        fact is one shape whatever kind of figure it carries.
//   NOT MINE AT ALL      `partner_reports`: real revenue that reaches us through
//                        a royalty statement. It is ON the screen because it is
//                        owed, and in NO total headed "to invoice".
//
// A FIGURE THAT IS NOT KNOWN IS DRAWN AS AN EMPTY SLOT, never as a zero and
// never as a dash. That is the one rendering rule this whole system is built
// around (0017, 0021), and it is the difference between "we are billing nothing"
// and "we do not know yet".
//
// NOTHING IS CONVERTED. A close is worked in the currency the document will be
// raised in, an invoice may not mix currencies, and a blended figure is a number
// nobody can send. So the summary carries one figure per currency and there is
// no FX footnote here — there is no rate to disclose.
//
// THE FIGURE IS WHAT GOES ON THE INVOICE: our share of what we sell and the
// partner's share of what we spend. Those are opposite halves of a split, and
// a column of "our share" added them into a total no document carried. A
// charge already on an issued invoice is not still to bill, so it is not here
// at all — ./invoiceFigure.ts says why the report keeps it anyway.
//
// COST RECOVERY FOLDS INTO ONE LINE. A metered deal recovers a dozen costs a
// month, one per meter, and read in full they bury the revenue the month is
// mostly about. The folded line is the group's subtotal; one press opens it.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  convertMinor,
  type DealFactKind,
  type MonthCloseCustomer,
  type MonthCloseLine,
  type MonthCloseReport,
} from "@crm/shared";
import styles from "./MonthClosePage.module.css";
import { FactEntryDialog, type FactPrefill } from "../facts/FactEntryDialog.js";
import {
  closeDefaultPeriod,
  periodToSearch,
  searchToDeal,
  searchToPeriod,
  shiftPeriod,
  type CloseSearch,
} from "./closeSearch.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyState, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { FigureProvenance } from "../../design/FigureProvenance.js";
import { FxDisplayFootnote } from "../../design/FxFootnote.js";
import { Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import {
  derivedGrowthKey,
  useDerivedGrowth,
  useMonthCloseReport,
  type DerivedGrowth,
  type DerivedGrowthQuery,
} from "../../api/queries.js";
import { useHasRole } from "../../auth/useAuth.js";
import { useCreateInvoiceDraftMutation } from "../../api/mutations.js";
import { useIdempotencyKey } from "../../api/idempotency.js";
import {
  formatCalendarDate,
  formatCalendarDateShort,
  formatCalendarMonth,
  formatMonth,
  parseCalendarMonth,
  todayIsoDate,
} from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatRate } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { assertNever } from "../../lib/assertNever.js";
import {
  billedSharePct,
  chargeGroups,
  groupKindOf,
  invoiceFigureMinor,
  invoiceTotalMinor,
  stillToBill,
  type ChargeGroup,
  type ChargeGroupKind,
} from "./invoiceFigure.js";
import { pricedByReading } from "./pricedBy.js";

/**
 * The line a person is supplying a figure for.
 *
 * ONE shape, where there used to be two. A usage reading and a cost base are
 * both a fact about a term line in a period, and the ingest path that records
 * them is one path — so the screen has one control and one dialog.
 */
interface Pricing {
  line: MonthCloseLine;
  prefill: FactPrefill;
}

export function MonthClosePage(): ReactNode {
  const search = useSearch({ from: "/billing/close" });
  const navigate = useNavigate();
  // Read once per render and threaded through, so the heading, the month
  // controls and the URL's default month cannot disagree about what day it is.
  const today = todayIsoDate();
  const period = useMemo(() => searchToPeriod(search, today, closeDefaultPeriod), [search, today]);
  const focusDeal = searchToDeal(search);

  const setPeriod = useCallback(
    (next: string) => {
      void navigate({
        to: "/billing/close",
        search: periodToSearch(next, today, closeDefaultPeriod) as CloseSearch,
      });
    },
    [navigate, today],
  );

  const [pricing, setPricing] = useState<Pricing | null>(null);
  const close = useMonthCloseReport(period, null, useReportCurrency());

  // The month DAY BY DAY, for the lines whose figure is still adding up.
  //
  // The report already says what such a line is worth; this says that it is
  // still moving, which is the fact a person reading a close has to know before
  // they treat the number as final. Asked only for the deals that carry one, so
  // an ordinary month — every figure stored — makes no extra read at all. A
  // partner-wide recharge belongs to no deal and therefore to no bound tenant,
  // so it keeps the words and loses only the picture.
  //
  // Never from the month being stepped off: while the next month loads, `data`
  // is the previous month's close (kept on screen, dimmed), and its deals asked
  // for the NEW period's usage would be reads for a figure nobody sees.
  const settledClose = close.isPlaceholderData ? undefined : close.data;
  const growthQueries = useMemo<DerivedGrowthQuery[]>(
    () => growingDeals(settledClose, period),
    [settledClose, period],
  );
  const growth = useDerivedGrowth(growthQueries);

  usePageChrome({
    title: "To bill",
  });

  // A BUSY MONTH TAKES SECONDS TO READ, and the screen owes the reader an
  // answer inside one. `showingAnother` is the state that has to be visible:
  // the month in the URL is the new one, and every figure on the page is still
  // the old one. Dimmed and named, rather than blank — a page that empties for
  // seven seconds reads as a page that broke.
  //
  // The picker is disabled for the whole read, not just the placeholder half.
  // A second press used to queue a second report, and whichever answered last
  // won: the reader landed on a month they had already stepped off.
  const showingAnother = close.isPlaceholderData;
  const busy = close.isFetching;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.monthPick}>
          <Button
            variant="secondary"
            onClick={() => setPeriod(shiftPeriod(period, -1))}
            aria-label="Previous month"
            disabled={busy}
          >
            ‹
          </Button>
          <span className={styles.monthLabel}>{formatMonth(period)}</span>
          <Button
            variant="secondary"
            onClick={() => setPeriod(shiftPeriod(period, 1))}
            aria-label="Next month"
            disabled={busy}
          >
            ›
          </Button>
        </div>
        {showingAnother && <Spinner label={`Loading ${formatMonth(period)}…`} inline />}
        <span className={styles.spacer} />
        {close.data !== undefined && (
          <span className={showingAnother ? styles.stale : undefined}>
            <Summary report={close.data} />
          </span>
        )}
        <Link to="/implementation" className={styles.toolbarLink}>
          Implementation →
        </Link>
        <Link to="/invoices" className={styles.toolbarLink}>
          Invoices →
        </Link>
      </div>

      <div
        className={showingAnother ? `${styles.results} ${styles.stale}` : styles.results}
        aria-busy={showingAnother}
      >
        <QueryBoundary query={close} label="what this month has to bill">
          {(report) => (
            <>
              <CustomerCards
                report={report}
                period={period}
                growth={growth}
                focusDeal={focusDeal}
                onPrice={setPricing}
              />
              {/* Only when something is in another currency than the reader's:
                  then the summary carries a converted figure, and this says
                  which rate made it. A footnote about a rate nobody used would
                  be noise. */}
              {report.totals.some((total) => total.currency !== report.currency) && (
                <FxDisplayFootnote fx={report.fx} generatedAt={report.generated_at} />
              )}
            </>
          )}
        </QueryBoundary>
      </div>

      {pricing !== null && (
        <FactEntryDialog prefill={pricing.prefill} onClose={() => setPricing(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The month in one line.
//
// ONE FIGURE PER CURRENCY, stacked — the same rule every group below follows,
// because each is a document that can be raised. When there are two, ONE
// converted figure follows them in the reader's report currency, marked "≈",
// so "what is the month worth" has an answer without any figure above being
// restated. The count beside them is what is still missing, because a total
// over a set with holes in it is the wrong-number-that-looks-right this whole
// design exists to remove.
// ---------------------------------------------------------------------------

function Summary({ report }: { report: MonthCloseReport }): ReactNode {
  const toInvoice = invoiceTotalsByCurrency(report);
  const missing = report.counts.need_counts.figure;
  const converted = convertedInvoiceTotal(toInvoice, report);

  return (
    <div className={styles.summary} role="group" aria-label="What this month is worth">
      {toInvoice.length === 0 ? (
        <span className={styles.summaryMoney}>nothing priced yet</span>
      ) : (
        toInvoice.map((total) => (
          <span key={total.currency} className={styles.summaryMoney}>
            {formatMoney(total.minor, total.currency)}
          </span>
        ))
      )}
      {converted !== null && (
        <span
          className={styles.summaryConverted}
          title={`Converted for display at USD→CAD ${formatRate(converted.rate)} as of ${formatCalendarDate(converted.asOf)}, the latest rate on file. Each invoice is raised in its own currency.`}
        >
          {`≈ ${formatMoney(converted.minor, report.currency)} in all`}
        </span>
      )}
      <span className={styles.summaryNote}>
        {missing === 0
          ? "to invoice · every line has its figure"
          : `to invoice · ${formatCount(missing)} still to enter`}
      </span>
    </div>
  );
}

/**
 * The figures above added up in the report currency, at the display rate the
 * report names in `fx`, each currency's total converted ONCE (the server's
 * rule for `totals_converted`). Null when there is nothing to add — one
 * currency, or that currency already the report's — or no rate to add with.
 */
function convertedInvoiceTotal(
  toInvoice: ReadonlyArray<{ currency: MonthCloseCustomer["currency"]; minor: number }>,
  report: MonthCloseReport,
): { minor: number; rate: number; asOf: string } | null {
  if (!toInvoice.some((total) => total.currency !== report.currency)) return null;
  const rate = report.fx.usd_to_cad;
  const asOf = report.fx.as_of;
  if (report.fx.status !== "display_converted" || rate === null || asOf === null) return null;
  const minor = toInvoice.reduce(
    (sum, total) => sum + convertMinor(total.minor, total.currency, report.currency, rate),
    0,
  );
  return { minor, rate, asOf };
}

/**
 * What the month's documents carry, one figure per currency, in the order the
 * report lists its currencies. Summed off the cards themselves, so the line at
 * the top cannot disagree with the cards under it.
 */
function invoiceTotalsByCurrency(
  report: MonthCloseReport,
): Array<{ currency: MonthCloseCustomer["currency"]; minor: number }> {
  const byCurrency = new Map<MonthCloseCustomer["currency"], number>();
  for (const group of report.customers) {
    const minor = invoiceTotalMinor(chargeGroups(stillToBill(group.lines)));
    byCurrency.set(group.currency, (byCurrency.get(group.currency) ?? 0) + minor);
  }
  return report.totals
    .map((total) => ({ currency: total.currency, minor: byCurrency.get(total.currency) ?? 0 }))
    .filter((total) => total.minor !== 0);
}

// ---------------------------------------------------------------------------
// One customer's month.
// ---------------------------------------------------------------------------

/**
 * The deals with a figure that is still adding up, deduplicated.
 *
 * A deal is asked about ONCE however many growing lines it carries, and a month
 * whose figures are all stored asks about nothing — the extra reads exist to
 * annotate a state most months are never in.
 */
function growingDeals(report: MonthCloseReport | undefined, period: string): DerivedGrowthQuery[] {
  if (report === undefined) return [];
  // Keyed by deal AND currency: a group is one currency, and its growth is
  // read back in it.
  const asked = new Map<string, DerivedGrowthQuery>();
  for (const group of report.customers) {
    if (group.deal_id === null) continue;
    const growing = group.lines.some(
      (line) => line.figure_source !== "contract" && line.period_open === true,
    );
    if (growing) {
      asked.set(`${group.deal_id}:${group.currency}`, {
        dealId: group.deal_id,
        period,
        currency: group.currency,
      });
    }
  }
  return [...asked.values()];
}

/**
 * One card per customer with something left to send.
 *
 * Two different empty months: one no clause covers, and one whose every charge
 * already went out. The second is a month with nothing left to DO, and saying
 * "nothing bills" there would read as a month that was never billed at all.
 */
function CustomerCards({
  report,
  period,
  growth,
  focusDeal,
  onPrice,
}: {
  report: MonthCloseReport;
  period: string;
  growth: ReadonlyMap<string, DerivedGrowth>;
  /** The deal a link came for, whose card is scrolled to and marked. */
  focusDeal: string | null;
  onPrice: (pricing: Pricing) => void;
}): ReactNode {
  if (report.customers.length === 0) {
    return (
      <Card>
        <EmptyState
          title={`Nothing bills for ${formatMonth(period)}`}
          description="No term line carries this month — nothing recurring, nothing metered and nothing to recover."
        />
      </Card>
    );
  }

  const cards = report.customers
    .map((group) => ({ group, lines: stillToBill(group.lines) }))
    .filter((card) => card.lines.length > 0);

  if (cards.length === 0) {
    return (
      <Card>
        <EmptyState
          title={`Nothing left to bill for ${formatMonth(period)}`}
          description="Every charge this month carries is already on an issued invoice."
        />
      </Card>
    );
  }

  return (
    <>
      {cards.map(({ group, lines }) => (
        <CustomerCard
          key={`${group.deal_id ?? group.partner_id}-${group.currency}`}
          group={group}
          lines={lines}
          period={period}
          growth={
            group.deal_id === null
              ? undefined
              : growth.get(derivedGrowthKey(group.deal_id, period, group.currency))
          }
          focused={focusDeal !== null && group.deal_id === focusDeal}
          onPrice={onPrice}
        />
      ))}
    </>
  );
}

function CustomerCard({
  group,
  lines,
  period,
  growth,
  focused,
  onPrice,
}: {
  group: MonthCloseCustomer;
  /** The customer's lines still to bill — the report's, less what already went out. */
  lines: readonly MonthCloseLine[];
  period: string;
  growth: DerivedGrowth | undefined;
  /** This card is the one a link asked for: scroll to it once and mark it. */
  focused: boolean;
  onPrice: (pricing: Pricing) => void;
}): ReactNode {
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) anchor.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [focused]);

  // A partner-level cost recovery belongs to no deal, so there is no customer to
  // lead with and the payer is genuinely what the card is about. Saying
  // `partner-wide` out loud is what keeps that from reading as missing data.
  const name = group.customer_name ?? group.partner_name;
  const partnerWide = group.customer_name === null;
  // An issued line always has its figure, so the report's pending count is
  // already a count over the lines still to bill.
  const priced = lines.length - group.pending_count;
  const groups = chargeGroups(lines);
  const invoiceMinor = invoiceTotalMinor(groups);
  const unbilledCount = groups.reduce((count, charges) => count + charges.unbilledCount, 0);

  return (
    <>
      <div ref={anchor} className={styles.cardAnchor} />
      <Card className={focused ? styles.focusedCard : undefined}>
        <CardHeader
          title={
            group.deal_id === null ? (
              name
            ) : (
              <Link
                to="/deals/$dealId"
                params={{ dealId: group.deal_id }}
                className={styles.cardTitleLink}
              >
                {name}
              </Link>
            )
          }
          description={
            partnerWide
              ? `${group.currency} · partner-wide · raised ${formatCalendarDate(group.due_date)}`
              : `via ${group.partner_name} · ${group.currency} · raised ${formatCalendarDate(group.due_date)}`
          }
          actions={
            <div className={styles.headerFigures}>
              {invoiceMinor !== 0 && (
                <>
                  <span className={styles.headerCaption}>{`To invoice ${group.partner_name}`}</span>
                  <span className={styles.money}>{formatMoney(invoiceMinor, group.currency)}</span>
                </>
              )}
              <PricedBadge lines={lines.length} priced={priced} />
              {invoiceMinor !== 0 && <DraftInvoiceButton group={group} period={period} />}
            </div>
          }
        />
        <Table label={`${name}, ${group.currency}`} className={styles.closeTable}>
          {/* The widths are set here rather than measured off the rows, so a
            group opening cannot move a column and every card on the page
            lines up with the one above it. */}
          <colgroup>
            <col />
            <col className={styles.colPricedBy} />
            <col className={styles.colFigure} />
            <col className={styles.colSplit} />
            <col className={styles.colFigure} />
          </colgroup>
          <thead>
            <tr>
              <Th>Charge</Th>
              <Th>Priced by</Th>
              <Th numeric>Amount</Th>
              <Th numeric>Split</Th>
              <Th numeric>On the invoice</Th>
            </tr>
          </thead>
          {groups.map((charges) => (
            <ChargeGroupRows
              key={charges.kind}
              charges={charges}
              customer={group}
              period={period}
              growth={growth}
              onPrice={onPrice}
            />
          ))}
          {/* A total of one line is the line again, so it appears the moment a
            customer's month is more than one charge — or the moment the total
            has something to EXPLAIN. A single royalty-reported line is the
            case: the figure it adds up to is nothing, and saying so beside the
            money that reaches us by statement is the whole argument. */}
          {(lines.length > 1 || unbilledCount > 0 || group.not_invoiced_count > 0) && (
            <tfoot>
              <tr>
                <Td colSpan={4}>
                  <span className={styles.totalLabel}>
                    {footLabel(group.partner_name, period, group.due_date)}
                  </span>
                  <span className={styles.totalNote}>{footNote(groups, unbilledCount, group)}</span>
                </Td>
                <Td numeric className={styles.invoiceTotal}>
                  {formatMoney(invoiceMinor, group.currency, { omitCurrencyCode: true })}
                </Td>
              </tr>
            </tfoot>
          )}
        </Table>
      </Card>
    </>
  );
}

/**
 * "Draft invoice": the close's own figures, as the document that goes out.
 *
 * The server builds the draft from this very close (one linked line per
 * billable charge, our share on the paper) and is idempotent per customer and
 * month, so pressing it again opens the draft that exists. The invoice page is
 * the one laid out for QuickBooks: who it goes to, each line, and a copy.
 */
function DraftInvoiceButton({
  group,
  period,
}: {
  group: MonthCloseCustomer;
  period: string;
}): ReactNode {
  const isEditor = useHasRole("crm_editor");
  const draft = useCreateInvoiceDraftMutation();
  const { keyFor } = useIdempotencyKey();
  const navigate = useNavigate();
  if (!isEditor) return null;

  const open = (): void => {
    const body = {
      ...(group.deal_id === null ? { partner_id: group.partner_id } : { deal_id: group.deal_id }),
      period,
      currency: group.currency,
    };
    draft.mutate(
      { body, idempotencyKey: keyFor(body) },
      {
        onSuccess: (result) => {
          void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: result.invoice.id } });
        },
      },
    );
  };

  return (
    <>
      <Button variant="primary" onClick={open} disabled={draft.isPending}>
        {draft.isPending ? "Drafting…" : "Draft invoice"}
      </Button>
      {draft.error !== null && <ProblemMessage error={draft.error} action="draft this invoice" />}
    </>
  );
}

const GROUP_NAME: Record<ChargeGroupKind, string> = {
  revenue: "Revenue",
  cost_recovery: "Cost recovery",
};

/** Teal is "a cost of ours" app-wide (ChargeMix), so cost recovery wears it here too. */
const GROUP_TONE: Record<ChargeGroupKind, Tone> = {
  revenue: "slate",
  cost_recovery: "teal",
};

/** Whose share of this kind of charge goes on the invoice, as the group's caption says it. */
function invoicedShare(kind: ChargeGroupKind, partner: string): string {
  switch (kind) {
    case "revenue":
      return "our share";
    case "cost_recovery":
      return `${partner}'s share`;
    default:
      return assertNever(kind, "charge group");
  }
}

/** Whose percentage the Split column shows, as the word under it. */
function shareHolder(kind: ChargeGroupKind, partner: string): string {
  switch (kind) {
    case "revenue":
      return "ours";
    case "cost_recovery":
      return `${partner}'s`;
    default:
      return assertNever(kind, "charge group");
  }
}

/**
 * One group of a customer's charges: a line that is the group's subtotal, and
 * the charges under it while the group is open.
 *
 * The subtotal sits ON the group's own line rather than under its charges, so
 * folding a group leaves exactly one line and the figures do not move when it
 * opens. Revenue opens and cost recovery starts folded: a metered month
 * recovers a dozen costs, and the handful of sales it is mostly about should
 * not be the part you scroll to.
 */
function ChargeGroupRows({
  charges,
  customer,
  period,
  growth,
  onPrice,
}: {
  charges: ChargeGroup;
  customer: MonthCloseCustomer;
  period: string;
  growth: DerivedGrowth | undefined;
  onPrice: (pricing: Pricing) => void;
}): ReactNode {
  const [open, setOpen] = useState(charges.kind === "revenue");
  const count = charges.lines.length;
  const unbilled = charges.unbilledCount;

  return (
    <tbody>
      <tr className={styles.groupRow}>
        <Td colSpan={2}>
          <div className={styles.groupHead}>
            <button
              type="button"
              className={styles.groupToggle}
              aria-expanded={open}
              onClick={() => setOpen((wasOpen) => !wasOpen)}
            >
              <Chevron open={open} />
              <Badge shape="tag" tone={GROUP_TONE[charges.kind]}>
                {GROUP_NAME[charges.kind]}
              </Badge>
            </button>
            <span className={styles.groupCaption}>
              {`we invoice ${invoicedShare(charges.kind, customer.partner_name)} · ${formatCount(count)} ${count === 1 ? "charge" : "charges"}`}
            </span>
          </div>
        </Td>
        <Td numeric muted>
          {formatMoney(charges.amountMinor, customer.currency, { omitCurrencyCode: true })}
        </Td>
        <Td numeric muted>
          <SplitFigure
            pct={charges.sharePct}
            holder={shareHolder(charges.kind, customer.partner_name)}
          />
        </Td>
        <Td numeric>
          <span className={styles.invoiceFigure}>
            {formatMoney(charges.invoiceMinor, customer.currency, { omitCurrencyCode: true })}
          </span>
          {/* Said on the folded line too: the control that supplies the figure
              is one press away, and a folded group must not hide that it has
              work in it. */}
          {unbilled > 0 && (
            <span className={styles.shortfall}>
              {`${formatCount(unbilled)} ${unbilled === 1 ? "needs" : "need"} a figure`}
            </span>
          )}
        </Td>
      </tr>
      {open &&
        charges.lines.map((line) => (
          <LineRow
            key={`${line.kind}-${line.id}`}
            line={line}
            group={customer}
            period={period}
            growth={growth}
            onPrice={onPrice}
          />
        ))}
    </tbody>
  );
}

function Chevron({ open }: { open: boolean }): ReactNode {
  return (
    <svg
      className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
    >
      <path
        d="M4.5 2.5 8 6l-3.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** "30%" over "NorthStar's"; `varies` where a group's lines disagree; nothing where none resolved. */
function SplitFigure({
  pct,
  holder,
}: {
  pct: number | "varies" | null;
  holder: string;
}): ReactNode {
  if (pct === null) return null;
  if (pct === "varies") return "varies";
  return (
    <>
      {`${pct}%`}
      <span className={styles.sub}>{holder}</span>
    </>
  );
}

/**
 * "Invoice to NorthStar · August 2026 · raised Sep 1".
 *
 * THE MONTH THE CHARGES ARE FOR COMES FIRST, and the raise date comes second.
 * The foot used to print the month of the DUE date, which on anything billed in
 * arrears is the next month: the August close said "Kingsport's Sep", against a
 * page titled "Everything that bills for Aug 2026". Two months on one card, and
 * neither said which was which.
 *
 * It names the PAYER, because the figure beside it is the document: the one
 * thing on the card that is addressed to somebody.
 */
function footLabel(payer: string, period: string, dueDate: string): string {
  return `Invoice to ${payer} · ${monthTitle(period)} · raised ${formatCalendarDateShort(dueDate)}`;
}

/** "August 2026". The month in full, as a card's own heading reads. */
function monthTitle(period: string): string {
  const month = parseCalendarMonth(period);
  return month === null ? formatMonth(period) : formatCalendarMonth(month);
}

/**
 * How the total adds up, and what it left out, in words.
 *
 * Two different exclusions, and they are not the same claim: a line with no
 * figure is money we expect and cannot state, while `not_invoiced` money is
 * money we are certain of and are not raising a document for. Reporting them as
 * one number would put the overstatement `Collection` exists to remove straight
 * back in.
 */
function footNote(
  groups: readonly ChargeGroup[],
  unbilledCount: number,
  group: MonthCloseCustomer,
): string {
  const parts: string[] = [];
  if (groups.length > 1) {
    parts.push(
      groups
        .map(
          (charges) =>
            `${GROUP_NAME[charges.kind].toLowerCase()} ${formatMoney(charges.invoiceMinor, group.currency, { omitCurrencyCode: true })}`,
        )
        .join(" + "),
    );
  }
  parts.push(
    unbilledCount === 0
      ? "every line has its figure"
      : `${formatCount(unbilledCount)} line${unbilledCount === 1 ? " has" : "s have"} no figure, and ${unbilledCount === 1 ? "is" : "are"} in no total`,
  );
  if (group.not_invoiced_count > 0) {
    parts.push(
      `${formatMoney(group.not_invoiced_minor, group.currency, { omitCurrencyCode: true })} more reaches us by statement, and is not in this figure`,
    );
  }
  return parts.join(" · ");
}

function PricedBadge({ lines, priced }: { lines: number; priced: number }): ReactNode {
  if (priced === lines) {
    return (
      <Badge tone="good" shape="chip">
        Ready
      </Badge>
    );
  }
  return (
    <Badge tone="warn" shape="chip">
      {priced === 0
        ? "nothing priced yet"
        : `${formatCount(priced)} of ${formatCount(lines)} priced`}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// One charge.
// ---------------------------------------------------------------------------

/**
 * The control that finishes this line, or none.
 *
 * A figure the facts already supplied is a figure, so no control appears beside
 * one: offering it would invite somebody to overwrite an answer with a guess —
 * and a person's fact beats every feed, so the guess would then be what the
 * invoice says.
 *
 * A line with no term line behind it is a standalone charge somebody typed onto
 * a document. There is no rule for a fact to feed, so there is nothing to
 * record.
 */
function pricingFor(
  line: MonthCloseLine,
  group: MonthCloseCustomer,
  period: string,
): Pricing | null {
  if (line.gross_minor !== null) return null;
  if (line.term_line_id === null) return null;
  return {
    line,
    prefill: {
      dealId: group.deal_id,
      partnerId: group.partner_id,
      termLineId: line.term_line_id,
      period,
      kind: factKindFor(line),
      unit: line.unit,
      currency: line.currency,
      label: line.label,
    },
  };
}

/**
 * Which kind of fact this line is short of.
 *
 * The clause's PRICE decides it, not its unit. A clause that states a unit
 * price is short of the count, and the rate turns that count into money. A
 * clause that states no unit price takes its figure from the facts, so what it
 * is short of is the money itself — and a count recorded there is a fact the
 * interpretation never reads, which leaves the line as blank as it was.
 *
 * `rate_minor` is that price, and it is what the report carries: it is null
 * exactly where the clause states no unit price. A unit of `period` or
 * `currency_minor` counts no units at all, so it takes an amount whatever the
 * clause says.
 *
 * Both kinds are one `deal_facts` row and one form; the kind decides which
 * field the form opens on.
 */
function factKindFor(line: MonthCloseLine): DealFactKind {
  const unit = line.unit;
  if (unit === null || unit === "currency_minor" || unit === "period") return "amount";
  return line.rate_minor === null ? "amount" : "quantity";
}

/**
 * What a line is waiting on, as a sentence — or nothing, when it waits on
 * nothing. This is the need vocabulary, moved out of a column of chips and
 * onto the row it describes.
 */
function waitingNote(line: MonthCloseLine): string | null {
  switch (line.need) {
    case "not_invoiced":
      return "reaches us through the partner's statement — we raise no document for it";
    case "nothing_to_bill":
      // A CERTAIN zero, and not a missing figure. The clause covered the month
      // and the facts measured nothing in it, so the row is evidence that the
      // month was read — it is in no total and there is nothing to raise.
      return "the month measured nothing — there is nothing to bill";
    case "unreached":
      return "the deliverable has not landed — waiting on delivery, not on paperwork";
    case "unresolved":
      // A period the facts priced in another currency also reads as
      // `unresolved`, and the agreement sentence would be a second, wrong
      // answer to the question `FigureProvenance` has already answered beside
      // the figure it withheld.
      return line.derived_issue !== null
        ? null
        : "priced, but no agreement term covers it — nothing can say what share is ours";
    case "invoiced":
      return line.invoice_number === null ? "already went out" : `invoice ${line.invoice_number}`;
    case "figure":
    case "ready":
      return null;
    default:
      return assertNever(line.need, "month close need");
  }
}

function LineRow({
  line,
  group,
  period,
  growth,
  onPrice,
}: {
  line: MonthCloseLine;
  group: MonthCloseCustomer;
  period: string;
  /** The month day by day, when this line's figure is still adding up. */
  growth: DerivedGrowth | undefined;
  onPrice: (pricing: Pricing) => void;
}): ReactNode {
  const canEdit = useHasRole("crm_editor");
  const pricing = pricingFor(line, group, period);
  const note = waitingNote(line);
  const priced = pricedByReading(line);
  // No `unpriced` here on purpose: the counts a line could not price are one
  // claim, and this row states them in the Priced by cell beside the reading
  // that explains them. Passing them here too printed them a second time,
  // under the amount, in a warning colour a refused fact does not deserve.
  const provenance = (
    <FigureProvenance
      source={line.figure_source}
      periodOpen={line.period_open}
      issue={line.derived_issue}
      growth={growth?.days}
      formatValue={(minor) => formatMoney(minor, line.currency, { omitCurrencyCode: true })}
    />
  );

  return (
    <tr>
      <Td strong>
        {/* THE CLAUSE NOTE IS NOT A LINE ITEM. It is context somebody wrote for
            whoever reads the clause next, and printed in full it pushed the
            charge itself out of the reader's way. It rides on the hover here,
            and it is written out under `details` below. */}
        {line.note === null ? line.label : <span title={line.note}>{line.label}</span>}
        {line.milestone_label !== null && (
          <span className={styles.sub}>{`Milestone — ${line.milestone_label}`}</span>
        )}
        {note !== null && <span className={styles.sub}>{note}</span>}
      </Td>
      <Td className={styles.pricedBy}>
        <span className={styles.reading}>{priced.price}</span>
        {priced.unpriced !== null && (
          <span className={priced.unpriced.warn ? styles.shortfall : styles.sub}>
            {priced.unpriced.text}
          </span>
        )}
        {/* The interpretation's own sentence, kept and closed. It says which
            arm priced each fact and how many the selector refused, which is
            what a reader checking a figure asks for second — after they have
            found the row. */}
        <details className={styles.basis}>
          <summary className={styles.basisSummary}>details</summary>
          <span className={styles.basisText}>{line.basis}</span>
          {line.note !== null && <span className={styles.basisText}>{line.note}</span>}
        </details>
      </Td>
      <Td numeric muted>
        {line.gross_minor !== null ? (
          formatMoney(line.gross_minor, line.currency, { omitCurrencyCode: true })
        ) : pricing !== null && canEdit ? (
          <EntrySlot pricing={pricing} onPrice={onPrice} />
        ) : (
          <span className={styles.unknown}>no figure</span>
        )}
        {/* Under the figure whatever the figure turned out to be: it is the
            mark that tells a growing number from a settled one, and it is also
            where a WITHHELD figure says why there is none. Silent on the
            ordinary case, so most rows carry nothing here. */}
        {provenance}
      </Td>
      <Td numeric muted>
        <SplitFigure
          pct={billedSharePct(line)}
          holder={shareHolder(groupKindOf(line), group.partner_name)}
        />
      </Td>
      <Td numeric>
        <InvoiceFigure line={line} />
      </Td>
    </tr>
  );
}

function InvoiceFigure({ line }: { line: MonthCloseLine }): ReactNode {
  // Owed, and on no document of ours. The sentence under the charge says why.
  if (line.need === "not_invoiced") return <span className={styles.sub}>by statement</span>;
  const figure = invoiceFigureMinor(line);
  // The empty box again, and this time with nothing in it: the amount cell
  // beside it has already said "no figure" for this row, and a second copy of
  // those words is noise rather than information.
  if (figure === null) return <span className={styles.unknown} aria-hidden="true" />;
  return (
    <span className={styles.invoiceFigure}>
      {formatMoney(figure, line.currency, { omitCurrencyCode: true })}
    </span>
  );
}

/**
 * The empty slot, as a control.
 *
 * Drawn as the field it is about to become rather than as a button beside one,
 * because the thing the reader is looking for in this column is a NUMBER, and
 * the honest way to say "there is a number missing here" is a box the shape of
 * the number with nothing in it. The unit rides alongside so the box says what
 * it wants without being clicked.
 */
function EntrySlot({
  pricing,
  onPrice,
}: {
  pricing: Pricing;
  onPrice: (pricing: Pricing) => void;
}): ReactNode {
  const unit = pricing.prefill.kind === "amount" ? "amount" : (pricing.prefill.unit ?? "usage");

  return (
    <button
      type="button"
      className={styles.slot}
      onClick={() => onPrice(pricing)}
      aria-label={`Record a fact: ${unit}`}
    >
      <span className={styles.slotField} aria-hidden="true" />
      <span className={styles.slotUnit}>{unit}</span>
    </button>
  );
}
